<ph-chat-gutter [ngSwitch]="view">

    <ph-stack *ngSwitchCase="'list'" class="workflows-list">
        <ph-page-header *ngIf="listLoaded && !panelEmpty && !listLoadFailed" dense size="small" [level]="2">
            Your workflows
            <ph-badge slot="title-actions" class="workflows-count-row" variant="lighter" color="gray" size="small">{{ workflows.length }}</ph-badge>
            <ph-button slot="actions" icon="plus" size="small" aria-label="New workflow" data-test="new-workflow" (click)="openCreate()">New</ph-button>
        </ph-page-header>

        <ph-skeleton *ngIf="!listLoaded" shape="list" rows="3" label="Loading workflows"></ph-skeleton>

        <ph-empty-state *ngIf="listLoadFailed && !loading" icon="warning" heading="Couldn't load your workflows" center role="alert">
            <ph-button slot="actions" variant="secondary" mode="filled" (click)="loadList()">Try again</ph-button>
        </ph-empty-state>

        <ph-stack gap="snug" *ngIf="listLoaded && !panelEmpty && !listLoadFailed">
            <ph-list-row *ngFor="let w of workflows; trackBy: trackById"
                         class="workflow-row"
                         variant="card"
                         wrap
                         [heading]="w.title"
                         [meta]="outcomes(w)"
                         [muted]="w.status === 'paused'"
                         [attr.data-running]="isRunning(w) ? '' : null"
                         (ph-select)="openRuns(w)">
                <ph-stack slot="detail" gap="tight">
                    <ph-toolbar gap="tight">
                        <ph-tag variant="gray" [icon]="triggerIcon(w)" [attr.title]="cadence(w)" data-test="cadence">{{ cadence(w) }}</ph-tag>
                        <ph-tag *ngIf="skillName(w) as skill" variant="stroke" icon="scroll" data-test="skill-chip">{{ skill }}</ph-tag>
                    </ph-toolbar>
                    <ph-toolbar nowrap gap="tight">
                        <ph-status-badge *ngIf="isRunning(w)" status="pending" variant="light">{{ runningBadge(w) }}</ph-status-badge>
                        <ph-status-badge *ngIf="!isRunning(w) && lastRunFailed(w)" status="failed" variant="light">Last run failed</ph-status-badge>
                        <ph-hint data-test="last-run">{{ lastRunLine(w) }}</ph-hint>
                    </ph-toolbar>
                </ph-stack>
                <ph-status-badge slot="actions" [status]="workflowStatus(w)" variant="light">{{ statusLabel(w) }}</ph-status-badge>
                <ph-dropdown slot="actions"
                             label="Workflow options"
                             trigger-variant="ghost"
                             trigger-size="large"
                             [items]="rowMenuItems(w)"
                             [disabled]="isBusy(w)"
                             (ph-select)="onRowMenuSelect(w, $event.detail)"></ph-dropdown>
            </ph-list-row>
        </ph-stack>


        <ph-empty-state *ngIf="panelEmpty" icon="lightning-a" heading="No workflows yet" center>
            Tell Gable what to do and when. It asks a question or two, then runs on schedule and tells you what it found.
            <ph-button slot="actions" icon="plus" data-test="new-workflow" (click)="openCreate()">New workflow</ph-button>
        </ph-empty-state>
    </ph-stack>

    <ph-stack *ngSwitchCase="'editor'" class="workflows-editor">
        <ph-link-button class="back-link" icon="arrow-left" variant="gray" (click)="backToList()">All workflows</ph-link-button>
        <app-gable-workflow-editor [workflow]="editing"
                                   (saved)="onEditorSaved($event)"
                                   (cancel)="backToList()"></app-gable-workflow-editor>
    </ph-stack>

    <ph-stack *ngSwitchCase="'runs'" class="workflows-runs">
        <ph-link-button class="back-link" icon="arrow-left" variant="gray" (click)="backToList()">All workflows</ph-link-button>

        <ng-container *ngIf="runsWorkflow as w">
            <ph-stack gap="tight">
                <ph-page-header dense size="small" [level]="3" data-test="runs-header">
                    {{ w.title }}
                    <ph-status-badge slot="title-actions" variant="light" [status]="workflowStatus(w)">{{ statusLabel(w) }}</ph-status-badge>
                </ph-page-header>
                <ph-hint *ngIf="w.summary" data-test="runs-summary">{{ w.summary }}</ph-hint>
            </ph-stack>
            <ph-toolbar gap="tight">
                <ph-tag variant="gray" [icon]="triggerIcon(w)">{{ cadence(w) }}</ph-tag>
                <ph-tag *ngIf="skillName(w) as skill" variant="stroke" icon="scroll">{{ skill }}</ph-tag>
            </ph-toolbar>
            <ph-actions align="start">
                <ph-button *ngIf="isSuperUser && runs.length > 0" variant="secondary" mode="filled" size="small" icon="play" data-test="runs-run-now" [disabled]="isBusy(w) || isRunning(w) || isEventTriggered(w)" (click)="runNow(w)">Run now</ph-button>
                <ph-dropdown trigger-label="More" align="start" [items]="detailMenuItems(w)" [disabled]="isBusy(w)" (ph-select)="onRowMenuSelect(w, $event.detail)"></ph-dropdown>
            </ph-actions>
            <ph-hint *ngIf="isSuperUser && isEventTriggered(w)">{{ runNowTooltip(w) }}</ph-hint>

            <ph-divider></ph-divider>
            <ph-stack>
                <ph-page-header dense size="small" [level]="4">Runs</ph-page-header>

                <ph-stack gap="snug" *ngIf="runs.length > 0">
                    <ph-list-row *ngFor="let run of runs; trackBy: trackById"
                                 data-test="run-toggle"
                                 variant="card"
                                 wrap
                                 [heading]="runWhen(run)"
                                 [meta]="runLine(run)"
                                 [aside]="runDuration(run)"
                                 [open]="isRunExpanded(run)" expandable
                                 (ph-select)="toggleRun(run)">
                        <ph-status-badge slot="actions" [status]="runStatus(run)" variant="light">{{ runStatusLabel(run) }}</ph-status-badge>
                        <ph-stack slot="detail" gap="tight" *ngIf="isRunExpanded(run) || runTriggerLabel(run) || runDocumentUrl(run)">
                            <ph-toolbar *ngIf="runTriggerLabel(run) as label" data-test="run-trigger">
                                <ph-hint>Triggered by</ph-hint>
                                <ph-link-button *ngIf="runTriggerUrl(run) as url; else triggerText" icon="lightning" [attr.href]="url" [attr.target]="isAppUrl(url) ? null : '_blank'" (click)="onRunLinkClick($event, url)" data-test="run-trigger-link">{{ label }}</ph-link-button>
                                <ng-template #triggerText>
                                    <ph-hint data-test="run-trigger-text">{{ label }}</ph-hint>
                                </ng-template>
                                <ph-hint *ngIf="runTriggerCount(run) as count" data-test="run-trigger-count">{{ count }} {{ count === 1 ? 'record' : 'records' }}</ph-hint>
                            </ph-toolbar>
                            <ph-toolbar *ngIf="runDocumentUrl(run) as url" data-test="run-document">
                                <ph-link-button icon="file-pdf" [attr.href]="url" [attr.target]="isAppUrl(url) ? null : '_blank'" (click)="onRunLinkClick($event, url)" data-test="run-document-link">Filed to Documents</ph-link-button>
                            </ph-toolbar>
                            <ph-stack *ngIf="isRunExpanded(run)" gap="tight" data-test="run-detail">
                                <ph-rich-text *ngIf="run.summary; else noSummary" size="sm" [markdown]="run.summary"></ph-rich-text>
                                <ng-template #noSummary>
                                    <ph-hint>{{ runIsInFlight(run) ? 'The summary appears here when the run finishes.' : 'No summary was written for this run.' }}</ph-hint>
                                </ng-template>
                                <ph-actions align="start" *ngIf="(run.candidatesStaged ?? 0) > 0">
                                    <ph-button variant="secondary" mode="filled" size="small" data-test="see-suggestions" (click)="openActivity()">See suggestions in Activity</ph-button>
                                </ph-actions>
                            </ph-stack>
                        </ph-stack>
                    </ph-list-row>
                </ph-stack>

                <ph-skeleton *ngIf="runsLoading && runs.length === 0" shape="list" rows="3" label="Loading runs"></ph-skeleton>

                <ph-empty-state *ngIf="runsLoadFailed && !runsLoading" icon="warning" heading="Couldn't load the runs" center role="alert">
                    <ph-button slot="actions" variant="secondary" mode="filled" (click)="loadMoreRuns()">Try again</ph-button>
                </ph-empty-state>

                <ph-empty-state *ngIf="!runsLoading && !runsLoadFailed && runs.length === 0" icon="clock-counter-clockwise" heading="This workflow has not run yet" center data-test="runs-empty">
                    <ph-button *ngIf="isSuperUser" slot="actions" icon="play" data-test="runs-run-now" [disabled]="isBusy(w) || isRunning(w) || isEventTriggered(w)" (click)="runNow(w)">Run now</ph-button>
                </ph-empty-state>

                <ph-actions align="center" *ngIf="runsHaveMore && !runsLoadFailed">
                    <ph-button variant="secondary"
                               mode="filled"
                               data-test="load-more"
                               [disabled]="runsLoading"
                               (click)="loadMoreRuns()">{{ runsLoading ? 'Loading' : 'Load more' }}</ph-button>
                </ph-actions>
            </ph-stack>
        </ng-container>
    </ph-stack>

    <app-confirm-dialog [open]="removeConfirmOpen"
                         heading="Delete workflow"
                         confirmLabel="Delete"
                         [destructive]="true"
                         (closed)="onRemoveConfirmClosed($event)">
        <ng-container *ngIf="removingWorkflow as w">Gable will stop running <strong>{{ w.title }}</strong> ({{ cadence(w).toLowerCase() }}). Past runs stay in Activity.</ng-container>
    </app-confirm-dialog>
</ph-chat-gutter>
